/**
 * Types a rotating list of terms into the search field's placeholder.
 *
 * The prefix ("Search for ") is permanent. Everything after it -- the term and
 * its suffix ("...") -- is typed in one character at a time, held, then erased
 * one character at a time before the next term.
 *
 * Only `placeholder` is ever written. `value` is never touched, so predictive
 * search is completely unaffected.
 *
 * Implemented as a custom element so disconnectedCallback can clear the timer.
 * The theme editor re-renders a section on every setting change; a plain script
 * would leak one timer per edit and several animations would fight over the
 * same field.
 */
class TypewriterPlaceholder extends HTMLElement {
  connectedCallback() {
    this.tryInit();
  }

  /**
   * connectedCallback fires when the opening tag is connected, which can be
   * before the element's children have been parsed -- and reliably is when the
   * Section Rendering API re-inserts this section, as the theme editor does on
   * every setting change. A single querySelector in connectedCallback therefore
   * finds nothing and the component silently never starts.
   *
   * So: look for the input, and if it is not there yet, wait for the document
   * to finish parsing and try again, with a couple of animation frames as a
   * backstop for late insertion.
   */
  tryInit(attempt = 0) {
    if (this.initialised) return;

    const input = this.querySelector('input[type="search"]');

    if (!input) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.tryInit(), { once: true });
      } else if (attempt < 3) {
        requestAnimationFrame(() => this.tryInit(attempt + 1));
      }
      return;
    }

    this.init(input);
  }

  init(input) {
    this.initialised = true;
    this.input = input;

    // Not this.prefix: Element.prototype.prefix is a read-only DOM property
    // (an element's namespace prefix). Class bodies are strict mode, so
    // assigning to it throws a TypeError and silently aborts the rest of this
    // method. suffixText is named to match.
    // Belt and braces with the Liquid side: an HTML attribute can lose a
    // trailing space to a minifier, and the theme editor strips it from the
    // setting itself, which would run the prefix into the term.
    const rawPrefix = this.dataset.prefix || '';
    this.prefixText = rawPrefix && !rawPrefix.endsWith(' ') ? `${rawPrefix} ` : rawPrefix;
    this.suffixText = this.dataset.suffix || '';
    this.terms = (this.dataset.terms || '')
      .split(/\r?\n/)
      .map((term) => term.trim())
      .filter(Boolean);

    if (this.terms.length === 0) return;

    this.typeSpeed = parseInt(this.dataset.speed, 10) || 70;
    this.holdTime = parseInt(this.dataset.hold, 10) || 1800;
    // Erasing reads better a little faster than typing.
    this.eraseSpeed = Math.max(20, Math.round(this.typeSpeed / 2));

    // A field that already has a value means a search is in progress, e.g. the
    // search results page. Leave the merchant's query alone.
    if (this.input.value) return;

    // Reduced motion keeps the server-rendered placeholder, which is already a
    // complete, readable phrase.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.termIndex = 0;
    this.charIndex = 0;
    this.erasing = false;
    this.stopped = false;
    this.timer = null;

    this.stop = this.stop.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    // A placeholder moving while someone is trying to type is a nuisance, so
    // the first focus or keystroke ends the animation for good.
    this.input.addEventListener('focus', this.stop, { once: true });
    this.input.addEventListener('input', this.stop, { once: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.schedule(this.holdTime);
  }

  disconnectedCallback() {
    this.teardown();
    // Allow a re-inserted element to start again. Deliberately not reset in
    // teardown(), which stop() also calls -- a visitor who has used the field
    // should not have the animation come back.
    this.initialised = false;
  }

  teardown() {
    clearTimeout(this.timer);
    this.timer = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.input?.removeEventListener('focus', this.stop);
    this.input?.removeEventListener('input', this.stop);
  }

  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.teardown();
    // Leave a whole phrase behind rather than a half-typed word.
    this.input.placeholder = this.prefixText + this.terms[0] + this.suffixText;
  }

  onVisibilityChange() {
    if (this.stopped) return;

    if (document.hidden) {
      clearTimeout(this.timer);
      this.timer = null;
    } else if (!this.timer) {
      this.schedule(this.typeSpeed);
    }
  }

  schedule(delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.tick(), delay);
  }

  tick() {
    if (this.stopped) return;

    // The suffix is typed and erased along with the term, so it animates in
    // exactly the same way.
    const phrase = this.terms[this.termIndex] + this.suffixText;

    if (this.erasing) {
      this.charIndex -= 1;
      this.render(phrase.slice(0, this.charIndex));

      if (this.charIndex <= 0) {
        this.erasing = false;
        this.termIndex = (this.termIndex + 1) % this.terms.length;
        this.schedule(this.typeSpeed * 3);
      } else {
        this.schedule(this.eraseSpeed);
      }
      return;
    }

    this.charIndex += 1;
    this.render(phrase.slice(0, this.charIndex));

    if (this.charIndex >= phrase.length) {
      this.erasing = true;
      this.schedule(this.holdTime);
    } else {
      this.schedule(this.typeSpeed);
    }
  }

  render(text) {
    this.input.placeholder = this.prefixText + text;
  }
}

if (!customElements.get('typewriter-placeholder')) {
  customElements.define('typewriter-placeholder', TypewriterPlaceholder);
}
